import { useState } from 'react';
import type { Item } from '../db/types';
import { formatModified } from '../lib/dates';
import { backlinksTo, outlineOf } from '../lib/outline';
import './SidePanel.css';

interface Props {
  item: Item;
  allItems: Item[];
  onGoToLine: (line: number) => void;
  onOpenItem: (item: Item) => void;
  onClose: () => void;
}

type Tab = 'outline' | 'backlinks';

/** Quick navigation through headings, plus what else points at this note. */
export function OutlinePanel({ item, allItems, onGoToLine, onOpenItem, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('outline');
  const headings = outlineOf(item.body);
  const backlinks = backlinksTo(item, allItems);

  return (
    <aside className="side side--left" aria-label="Outline">
      <div className="side__tabs">
        <button
          type="button"
          className={`side__tab${tab === 'outline' ? ' is-active' : ''}`}
          onClick={() => setTab('outline')}
        >
          Outline
        </button>
        <button
          type="button"
          className={`side__tab${tab === 'backlinks' ? ' is-active' : ''}`}
          onClick={() => setTab('backlinks')}
        >
          Backlinks
          {backlinks.length > 0 && <span className="side__badge">{backlinks.length}</span>}
        </button>
        <span className="spacer" />
        <button type="button" className="side__close" onClick={onClose} aria-label="Close outline">
          ✕
        </button>
      </div>

      <div className="side__body">
        {tab === 'outline' ? (
          headings.length === 0 ? (
            <p className="side__empty">
              No headings yet. Start a line with <span className="mono">##</span> to add one.
            </p>
          ) : (
            headings.map((h) => (
              <button
                key={`${h.line}-${h.text}`}
                type="button"
                className="outline__row"
                style={{ paddingLeft: `${8 + (h.level - 1) * 14}px` }}
                onClick={() => onGoToLine(h.line)}
                title={h.text}
              >
                <span className="outline__level">H{h.level}</span>
                <span className="outline__text truncate">{h.text || 'Untitled heading'}</span>
              </button>
            ))
          )
        ) : backlinks.length === 0 ? (
          <p className="side__empty">
            Nothing links here. A note that mentions this one by title will show up.
          </p>
        ) : (
          backlinks.map((other) => (
            <button
              key={other.id}
              type="button"
              className="outline__row outline__row--link"
              onClick={() => onOpenItem(other)}
            >
              <span className="outline__text truncate">{other.title || 'Untitled'}</span>
              <span className="outline__when">{formatModified(other.updated_at)}</span>
            </button>
          ))
        )}
      </div>
    </aside>
  );
}
